import {
  postcodeValidator,
  postcodeValidatorExistsForCountry,
} from "postcode-validator";
import { POSTCODE_REGEXES } from "postcode-validator/lib/esm/postcode-regexes";

/**
 * Validate a postal code for a given country using the postcode-validator library.
 * Falls back to a loose check (1-10 alphanumeric chars) when the country is not supported.
 */
export const isPostalCodeValid = (
  value: string,
  countryCode?: string,
): boolean => {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;

  if (countryCode && postcodeValidatorExistsForCountry(countryCode)) {
    return postcodeValidator(trimmed, countryCode);
  }

  // Fallback: alphanumeric + spaces/hyphens, 1–10 chars
  return /^[A-Za-z0-9 -]{1,10}$/.test(trimmed);
};

/**
 * Build an iMask-compatible regex for a country's postal code input.
 *
 * Analyzes the validation regex to extract which character classes are used
 * (digits, letters, spaces, hyphens) and the approximate max length,
 * then produces a permissive partial-match regex for masking.
 */
export const getPostalCodeMaskRegex = (countryCode?: string): RegExp => {
  if (!countryCode) return /^[A-Za-z0-9 -]{0,10}$/;

  const regex = POSTCODE_REGEXES.get(countryCode);
  if (!regex) return /^[A-Za-z0-9 -]{0,10}$/;

  const source = regex.source;

  // Detect which character classes appear in the regex
  const hasLetters = /[A-Z]|\\w/i.test(source.replace(/\\d/g, ""));
  const hasDigits = /\\d|[0-9]/.test(source);
  const hasSpace = /\[ ?\]|\[ -\]|\(?\[ \]\)?|\\s/.test(source);
  const hasHyphen = /[[-]|\\-/.test(source) || source.includes("[ -]");

  // Estimate max length from the regex
  const maxLength = estimateMaxLength(source);

  // Build the character class
  let chars = "";
  if (hasDigits) chars += "0-9";
  if (hasLetters) chars += "A-Za-z";
  if (hasSpace) chars += " ";
  if (hasHyphen) chars += "\\-";

  // Fallback if we couldn't detect anything
  if (!chars) chars = "A-Za-z0-9 \\-";

  return new RegExp(`^[${chars}]{0,${maxLength}}$`);
};

/**
 * Estimate the maximum length a postal code can have from its validation regex.
 */
function estimateMaxLength(source: string): number {
  // Remove anchors and flags
  const s = source.replace(/^\^|\$$/g, "");

  // Replace common patterns with length estimates
  // Optional groups like ([ ])? or [ -]? count as 1
  // \d{N} counts as N, \d counts as 1
  // [A-Z]{N} counts as N
  let total = 0;

  // Match quantified groups: \d{3}, [A-Z]{2}, etc.
  const quantified = s.matchAll(
    /(?:\[[^\]]*\]|\\[dDwWsS]|[A-Za-z0-9])\{(\d+)(?:,(\d+))?\}/g,
  );
  for (const m of quantified) {
    total += Number.parseInt(m[2] ?? m[1], 10);
  }

  // If we found quantifiers, add some slack for optional separators
  if (total > 0) {
    // Count optional groups (...)? or [ ]?
    const optionals = (s.match(/\?\)?/g) ?? []).length;
    return total + optionals + 2; // +2 for safety margin
  }

  // Fallback: count fixed chars roughly
  return 10;
}
